// ===================================
// TRENCH QUEST - Image Prompt Builder
// Consistent style prompts for Imagen 3 scene generation
// ===================================

import type { GeneratedSituation } from '@/types'

/**
 * Art style definitions for consistent image generation
 */
export const ART_STYLES = {
  pixel_art: {
    name: 'Retro Pixel Art',
    prompt: `Retro pixel art style inspired by 1990s adventure games like Leisure Suit Larry and Gabriel Knight.
16-bit aesthetic with vibrant, saturated colors.
Crisp pixel boundaries, visible dithering effects.
Limited color palette per element, clear silhouettes.
Slightly satirical and humorous character proportions.
Detailed backgrounds with layered depth.
No anti-aliasing, maintaining sharp pixel edges.`,
  },

  comic: {
    name: 'Modern Comic',
    prompt: `Modern comic book illustration style with bold ink outlines.
Dynamic compositions with dramatic perspective.
Vibrant color palette with strong shadows and highlights.
Expressive characters with exaggerated poses.
Speed lines and impact effects where appropriate.
Clean, professional line work with halftone dot effects.
Cyberpunk and vaporwave color influences (neon pinks, cyans, purples).`,
  },

  realistic: {
    name: 'Cinematic Realistic',
    prompt: `Photorealistic digital art with cinematic lighting.
High detail rendering with professional photography quality.
Dramatic compositions with rule of thirds.
Rich color grading like a thriller movie.
Volumetric lighting, lens flares, depth of field.
Modern urban environments with luxury aesthetic.
Characters in stylish, expensive clothing.`,
  },

  vaporwave: {
    name: 'Vaporwave Aesthetic',
    prompt: `Vaporwave aesthetic with neon colors and retro-futuristic elements.
Pink, cyan, and purple color scheme dominating.
Grid patterns, geometric shapes, and glitch effects.
Roman busts, palm trees, and 80s technology.
Sunset gradients and chrome textures.
VHS artifacts and scan lines.
Surreal, dreamlike atmosphere with floating objects.
Japanese text elements for authenticity.`,
  },

  noir: {
    name: 'Neo-Noir',
    prompt: `Neo-noir style with heavy contrast and dramatic shadows.
Limited color palette with accent colors (usually red or cyan).
Venetian blind shadows and rain-slicked streets.
Silhouettes and mysterious figures.
Urban nighttime settings with neon signs.
Smoky atmospheres and moody lighting.
1940s detective aesthetic meets cyberpunk.`,
  },
}

/**
 * Location descriptions for scene generation
 */
export const LOCATION_PROMPTS: Record<string, string> = {
  // Trading & Work
  'trading-terminal': 'A futuristic trading floor with walls of holographic screens displaying charts, candles, and order books. Multiple monitors showing green and red numbers. Keyboards and energy drinks scattered everywhere.',

  'hacker-house': 'A chaotic Silicon Valley startup house converted to crypto command center. Whiteboards covered in tokenomics diagrams, beanbag chairs, pizza boxes, and multiple MacBooks with sticker-covered lids.',

  'degen-den': 'A dimly lit underground trading room with the glow of multiple monitors. Red and green candle charts reflected on faces. Energy drink cans and fast food wrappers. The walls covered in printed memes and token logos.',

  // Luxury
  'penthouse-day': 'A luxurious modern penthouse with floor-to-ceiling windows overlooking a city skyline. Minimalist expensive furniture, abstract art on walls, and natural daylight streaming in.',

  'penthouse-night': 'The same penthouse at night with city lights twinkling below. Ambient lighting, a glass of whiskey on the table, and the glow of a laptop screen.',

  'yacht-exterior': 'The deck of a mega-yacht on sparkling blue ocean water. Pristine white surfaces, luxury lounge furniture, and the horizon stretching to infinity.',

  'yacht-interior': 'Inside a lavish yacht cabin with mahogany wood, leather seating, and crystal glasses. Nautical touches mixed with modern luxury technology.',

  'lambo-dealership': 'A high-end exotic car dealership showroom. Gleaming Lamborghinis, Ferraris, and McLarens under dramatic spotlights. Glass walls and polished floors reflecting the supercars.',

  'lambo-driving': 'First-person view from inside a Lamborghini cockpit, driving down a coastal highway. The steering wheel, digital dash, and the road ahead through the windshield.',

  // Locations
  'singapore-marina': 'The Singapore Marina Bay skyline at night with the iconic triple towers of Marina Bay Sands, colorful light shows reflecting on the water, and luxury yachts in the foreground.',

  'dubai-downtown': 'Dubai downtown at night with the Burj Khalifa towering in the background, futuristic architecture, and the glow of luxury everywhere.',

  'miami-beach': 'South Beach Miami with art deco buildings, palm trees, and the ocean. Vibrant colors, convertibles parked on the street, and beautiful people.',

  'beach-day': 'A tropical paradise beach with crystal clear turquoise water, white sand, palm trees swaying, and a luxury beach cabana.',

  'conference-hall': 'A packed cryptocurrency conference hall with booths displaying token logos, crowds of people with lanyards, giant screens with presentations, and swag everywhere.',

  // Official
  'fed-building': 'An imposing government building with classical architecture - marble columns, grand stairs, and serious-looking security. The facade suggests power and authority.',

  'courthouse': 'A dramatic courthouse interior with dark wooden benches, American flag, judges bench elevated, and the weight of justice in the air.',

  'sec-office': 'A sterile government office with fluorescent lighting, stacks of paper documents, government seals on the wall, and the atmosphere of investigation.',

  // Social
  'nightclub-vip': 'The VIP section of an exclusive nightclub with bottle service, velvet ropes, strobe lights, and beautiful people dancing.',

  'rooftop-party': 'A rooftop party with city skyline views, string lights, a DJ booth, champagne towers, and well-dressed attendees.',

  'coffee-shop': 'A hipster coffee shop with exposed brick, hanging plants, people working on laptops, and the warm glow of Edison bulbs.',

  // Default
  'unknown': 'A mysterious location shrouded in shadow, suggesting intrigue and possibility.',
}

/**
 * Mood descriptions for atmosphere
 */
export const MOOD_PROMPTS: Record<string, string> = {
  dramatic: 'Intense and serious atmosphere with high stakes feeling. Strong directional lighting creating dramatic shadows. Colors are deep and saturated. The moment feels pivotal.',

  comedic: 'Lighthearted and humorous atmosphere. Bright, warm colors. Characters might have exaggerated expressions. Something absurd is happening but everyone is taking it seriously.',

  tense: 'Suspenseful atmosphere filled with uncertainty. Cool color temperature with occasional warm accents. Shadows hiding things. Characters on edge, something is about to happen.',

  romantic: 'Warm and intimate atmosphere with soft lighting. Sunset colors or candlelight. Depth of field focusing on connection. The world fades away around the subjects.',

  mysterious: 'Enigmatic atmosphere with fog, shadows, and secrets. Cool blue and purple tones. Hidden faces and obscured details. Questions hanging in the air.',

  triumphant: 'Victorious and powerful atmosphere. Golden lighting like a sunrise of success. Characters standing tall. Energy of achievement and celebration.',

  dire: 'Ominous and dangerous atmosphere with warning signs everywhere. Red warning tones mixed with dark shadows. Something has gone very wrong or is about to.',
}

/**
 * Character archetype descriptions
 */
export const CHARACTER_ARCHETYPES: Record<string, string> = {
  'whale-mysterious': 'A powerful figure in expensive minimalist clothing, face partially obscured. They exude wealth and power without trying. Probably has a fancy watch visible.',

  'whale-friendly': 'A wealthy individual with a warm smile, dressed casually expensive. They seem approachable despite their obvious success. Might be wearing a subtle flex like a Richard Mille.',

  'fed-official': 'A stern government official in a perfect suit, American flag pin, carrying documents. They represent authority and regulation. Neutral expression, calculating eyes.',

  'sec-agent': 'An investigator type in a government-issue suit, maybe holding a badge or folder. They are watching, evaluating, taking mental notes.',

  'influencer-male': 'A crypto bro influencer with styled hair, designer streetwear, and obvious accessories. Probably taking a selfie or pointing at something enthusiastically.',

  'influencer-female': 'A female crypto influencer in chic outfit, perfect makeup, posing naturally. Could be doing a TikTok or Instagram story. Confident and camera-aware.',

  'dealer-slimy': 'A sketchy character who seems too eager to help. Flashy but cheap-looking clothing. Darting eyes and a too-wide smile. Something about them feels off.',

  'insider-nervous': 'Someone with valuable information who looks scared to share it. Constantly checking over their shoulder. Dressed normally but sweating slightly.',

  'romantic-interest': 'An attractive potential love interest who seems genuinely interested. Well-dressed but not trying too hard. Eye contact and a slight smile.',

  'rival-trader': 'A competitor who views the player as a threat. Expensive clothes, arrogant posture, maybe smirking. Success is visible but so is insecurity.',

  'tech-founder': 'A tech startup founder in their signature outfit (hoodie or black turtleneck). Surrounded by energy of innovation. Might be gesturing while explaining something.',

  'old-money': 'Old wealth aristocrat who looks down on new crypto money. Conservative expensive clothing, family crest somewhere visible. Barely concealed disdain.',
}

/**
 * Build complete image prompt for Imagen 3
 */
export function buildImagePrompt(
  situation: GeneratedSituation,
  style: keyof typeof ART_STYLES = 'pixel_art'
): string {
  const stylePrompt = ART_STYLES[style]?.prompt || ART_STYLES.pixel_art.prompt
  const locationPrompt = LOCATION_PROMPTS[situation.location] || LOCATION_PROMPTS.unknown
  const moodPrompt = MOOD_PROMPTS[situation.mood] || MOOD_PROMPTS.dramatic

  // Build character descriptions
  const characterDescriptions = situation.characters
    .map(char => {
      const archetype = CHARACTER_ARCHETYPES[char.visualArchetype] || ''
      return `${char.title}: ${archetype || char.personality.join(', ')}`
    })
    .join('\n')

  // Extract key visual elements from tags
  const visualElements = situation.visualTags
    .filter(tag => !tag.startsWith('mood:') && !tag.startsWith('location:'))
    .join(', ')

  // Construct the full prompt
  return `${stylePrompt}

SCENE DESCRIPTION:
${situation.description.slice(0, 500)}

LOCATION:
${locationPrompt}

ATMOSPHERE:
${moodPrompt}

${characterDescriptions ? `CHARACTERS PRESENT:\n${characterDescriptions}` : ''}

${visualElements ? `ADDITIONAL VISUAL ELEMENTS:\n${visualElements}` : ''}

IMPORTANT INSTRUCTIONS:
- Create a single cohesive scene illustration
- No text, UI elements, or watermarks
- Focus on atmosphere and storytelling through visual composition
- The scene should feel like a moment frozen in time
- Suitable for a video game scene background
- Aspect ratio: 16:9 widescreen`
}

/**
 * Build a simpler prompt for quick scene generation
 */
export function buildQuickImagePrompt(
  location: string,
  mood: string,
  style: keyof typeof ART_STYLES = 'pixel_art'
): string {
  const stylePrompt = ART_STYLES[style]?.prompt || ART_STYLES.pixel_art.prompt
  const locationPrompt = LOCATION_PROMPTS[location] || LOCATION_PROMPTS.unknown
  const moodPrompt = MOOD_PROMPTS[mood] || MOOD_PROMPTS.dramatic

  return `${stylePrompt}

SCENE: ${locationPrompt}
MOOD: ${moodPrompt}

Create a video game background scene. No text or UI. 16:9 aspect ratio.`
}

/**
 * Get appropriate style for situation category
 */
export function getStyleForCategory(category: GeneratedSituation['category']): keyof typeof ART_STYLES {
  const styleMap: Record<string, keyof typeof ART_STYLES> = {
    power_play: 'noir',
    lifestyle: 'vaporwave',
    insider: 'comic',
    romance: 'realistic',
    crisis: 'noir',
    recovery: 'pixel_art',
    market: 'comic',
    random: 'pixel_art',
  }

  return styleMap[category] || 'pixel_art'
}
